// istanbul ignore file
import { alpha } from '@mui/material/styles';
import { type Theme } from '@mui/system/createTheme/createTheme';

import { CONTAINER_MAX_WIDTH } from 'reservations/base/helpers';
import { LIGHT_SOY, WHITE } from 'reservations/brand/helpers/v1/palette';

export const getComponentsStyles = (palette: Record<string, any>) => ({
  muiButtonStyleOverride: {
    backgroundColor: palette.button?.background.main,
    color: palette.button?.font.main,
    '&:hover': {
      backgroundColor: palette.button?.background.hover,
      color: palette.button?.font.hover
    },
    '&:disabled': {
      backgroundColor: alpha(palette.button?.background.hover, 0.4),
      color: palette.button?.font.main
    }
  },
  muiLinkStyleOverride: {
    color: palette.link?.main,
    '&:hover': {
      color: palette.link?.hover
    }
  }
});

export const getAddressAndPhoneNumberBrandStyles = (theme: Theme) => ({
  color: WHITE,
  // @ts-ignore, https://github.com/mui/material-ui/issues/30678
  fontSize: theme.typography.body1.fontSize,
  // @ts-ignore
  fontWeight: theme.typography.body1.fontWeight,
  fontStyle: 'normal',
  paddingBottom: '2px'
});

export const getAvailabilitiesFormItemBrandStyles = () => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: WHITE // Notched outline
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.75)' // Outline on hover
    },
    '& .Mui-focused fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.35)' // Outline on focus
    },
    '& .MuiSvgIcon-root': {
      color: WHITE // Icon color
    },
    '&.Mui-disabled fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)' // Lighter border color when disabled (dropdown empty)
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.3)' // Lighter icon color when disabled (dropdown empty)
    }
  },
  '& .MuiInputLabel-root': {
    color: WHITE // Label color
  },
  '& .MuiInputLabel-root .Mui-focused': {
    color: WHITE // Label color on focus
  },
  '& .MuiInputBase-input': {
    color: WHITE // Input text color
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.3)' // Lighter input text color when disabled (dropdown empty)
  }
});

export const getAvailabilityButtonBrandStyles = () => ({
  position: 'relative',
  zIndex: 1,
  backgroundColor: WHITE,
  margin: '0 auto 16px',
  width: '90%',
  padding: '16px 0',
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.3)'
});

export const getWaitlistButtonTextBrandStyles = (
  theme: Theme,
  isSecondaryText: boolean
) => {
  let styles = {
    // @ts-ignore, https://github.com/mui/material-ui/issues/30678
    color: theme.palette.button?.font.main,
    fontWeight: 'bold',
    fontSize: '1.1rem',
    lineHeight: 'initial'
  };

  if (isSecondaryText) {
    styles = {
      ...styles,
      fontSize: '14px',
      fontWeight: 'light'
    };
  }

  return styles;
};

export const getBookingConfirmationDetailCardBrandStyles = () => ({
  background: 'none',
  borderRadius: '4px',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  gap: '16px',
  margin: '8px 0 63px'
});

export const getBookingConfirmationDetailHeaderBrandStyles = () => ({
  alignItems: 'flex-start',
  gap: '16px'
});

export const getConfirmationPageWrapperBrandStyles = () => ({
  padding: '0 16px'
});

export const getBookingDetailsCardWrapperBrandStyles = () => ({
  borderRadius: 'initial',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  p: 'initial'
});

export const getBookingDetailsCardContentBrandStyles = () => ({
  padding: '0',
  margin: '0 0 16px'
});

export const getBookingDetailsCardTextBrandStyles = () => ({
  color: WHITE
});

export const getSecondaryButtonBrandStyles = (theme?: Theme) => ({
  backgroundColor: 'transparent',
  borderRadius: '0',
  padding: '8px 20px',
  // @ts-ignore
  border: `1px solid ${theme?.typography.body1.color || WHITE}`,
  minWidth: 100
});

export const getLateCancellationPolicyTextBrandStyles = () => ({
  fontSize: '18px',
  textAlign: 'start'
});

export const getLocationListHeaderBrandStyles = (theme: Theme) => ({
  [theme.breakpoints.down('md')]: {
    marginTop: '40px'
  },
  color: WHITE,
  // @ts-ignore, https://github.com/mui/material-ui/issues/30678
  fontSize: theme.typography.h1.fontSize,
  // @ts-ignore
  fontWeight: theme.typography.h1.fontWeight,
  fontStyle: 'normal',
  padding: '10px 20px'
});

export const getLocationListTextBrandStyles = (theme: Theme) => ({
  color: WHITE,
  // @ts-ignore, https://github.com/mui/material-ui/issues/30678
  fontSize: theme.typography.body1.fontSize,
  // @ts-ignore
  fontWeight: theme.typography.body1.fontWeight,
  fontStyle: 'normal',
  padding: '10px 20px'
});

export const getMenuTabButtonBrandStyles = () => ({
  color: LIGHT_SOY,
  '&.Mui-disabled': {
    color: WHITE
  }
});

export const getConfirmationDetailsContainerBrandStyles = () => ({
  maxWidth: CONTAINER_MAX_WIDTH
});

export const getDrawerBrandStyles = (theme: Theme) => {
  return {
    backgroundColor: theme.palette.background.default
  };
};

export const getRadioColorBrandStyles = (theme: Theme) => ({
  '&': {
    // @ts-ignore
    color: theme.typography.body1.color
  },
  '&.Mui-checked, &.Mui-disabled': {
    color: theme.palette.button?.background.main
  }
});

export const getCustomTipInputBrandStyles = (theme: Theme) => ({
  // @ts-ignore
  color: theme.typography.body1.color,
  '&::before,': {
    // @ts-ignore
    borderBottom: `1px solid ${theme.typography.body1.color}`
  },
  '&:hover:not(.Mui-disabled, .Mui-error):before': {
    // @ts-ignore
    borderBottom: `2px solid ${theme.typography.body1.color}`
  }
});
